import React, { useEffect, useMemo, useRef, useState } from 'react';
import DashboardNav, { DashboardBrandRow } from './DashboardNav';
import {
  mcpDelete,
  mcpGet,
  mcpPost,
  mcpApiUrl,
  mcpStreamableConnectUrl,
  mcpCatalogHttpUrl,
  mcpEdgeStreamableUrl,
  mcpEdgeCatalogHttpUrl,
} from './mcpApi';
import {
  WALLET_TILES,
  connectWalletConnectEvm,
  getMetaMaskProvider,
  getWalletTile,
  isInjectedAvailable,
  resolveInjectedProvider,
} from './walletProviders';

const EVM_CHAIN_TO_HEX = {
  ethereum: '0x1',
  base: '0x2105',
  arbitrum: '0xa4b1',
  polygon: '0x89',
  bsc: '0x38',
};

const EVM_HEX_TO_CHAIN = Object.entries(EVM_CHAIN_TO_HEX).reduce((acc, [name, hex]) => {
  acc[hex.toLowerCase()] = name;
  return acc;
}, {});

const SOLANA_MAINNET_RPC = 'https://api.mainnet-beta.solana.com';
const EVM_PUBLIC_RPC = {
  ethereum: 'https://rpc.ankr.com/eth',
  base: 'https://mainnet.base.org',
  arbitrum: 'https://arb1.arbitrum.io/rpc',
  polygon: 'https://polygon-rpc.com',
  bsc: 'https://bsc-dataseed.binance.org',
};

function nativeSymbolForChain(chain) {
  switch (String(chain || '').toLowerCase()) {
    case 'solana':
      return 'SOL';
    case 'bsc':
      return 'BNB';
    case 'polygon':
      return 'MATIC';
    default:
      return 'ETH';
  }
}

function formatNativeAmount(value, decimals = 18, fractionDigits = 6) {
  try {
    const bigIntFn = typeof window !== 'undefined' && window.BigInt ? window.BigInt : null;
    if (!bigIntFn) {
      return null;
    }
    const raw = bigIntFn(value || 0);
    const base = bigIntFn(10) ** bigIntFn(decimals);
    const whole = raw / base;
    const frac = raw % base;
    if (frac === bigIntFn(0)) {
      return whole.toString();
    }
    const fracDigits = frac
      .toString()
      .padStart(decimals, '0')
      .slice(0, fractionDigits)
      .replace(/0+$/, '');
    return fracDigits ? `${whole.toString()}.${fracDigits}` : whole.toString();
  } catch (_) {
    return null;
  }
}

function isPendingAddress(address) {
  const a = String(address || '').trim();
  return !a || a.startsWith('pending_');
}

async function jsonRpcPost(url, method, params) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      jsonrpc: '2.0',
      id: 1,
      method,
      params,
    }),
  });
  const data = await res.json();
  if (data?.error) {
    throw new Error(data.error.message || 'RPC error');
  }
  return data?.result;
}

function metamaskDeepLinkForCurrentDapp() {
  if (typeof window === 'undefined') {
    return null;
  }
  const current = window.location.origin + window.location.pathname;
  return `https://link.metamask.io/dapp/${encodeURIComponent(current)}`;
}

/** Opens this origin in Phantom in-app browser (Solana-focused); see https://docs.phantom.com/phantom-deeplinks/other-methods/browse */
function phantomBrowseDeepLink() {
  if (typeof window === 'undefined') {
    return null;
  }
  const url = window.location.href;
  const ref = window.location.origin;
  return `https://phantom.app/ul/browse/${encodeURIComponent(url)}?ref=${encodeURIComponent(ref)}`;
}

function getPhantomSolana() {
  if (typeof window === 'undefined') {
    return null;
  }
  const p = window.phantom?.solana;
  return p?.isPhantom ? p : null;
}

function getPhantomEthereum() {
  if (typeof window === 'undefined') {
    return null;
  }
  const p = window.phantom?.ethereum;
  return p?.isPhantom ? p : null;
}

function buildConnectUrl(appId) {
  const t = (process.env.REACT_APP_MCP_CONNECT_URL_TEMPLATE || '').trim();
  if (t && appId) {
    return t.replace(/\{\{appId\}\}/g, encodeURIComponent(appId));
  }
  const edge = mcpEdgeStreamableUrl();
  if (edge) {
    return edge;
  }
  return mcpStreamableConnectUrl();
}

function McpAutomationEdgePanel({ appId }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [bearer, setBearer] = useState('');
  const [expires, setExpires] = useState('');
  const edgeStream = mcpEdgeStreamableUrl();
  const edgeCat = mcpEdgeCatalogHttpUrl();

  const generate = () => {
    setErr('');
    setLoading(true);
    void mcpPost('/v1/account-hub/mcp-edge/token', { app_id: appId })
      .then((data) => {
        setBearer((data && data.bearer_token) || '');
        setExpires((data && data.expires_at) || '');
      })
      .catch((e) => {
        setBearer('');
        setExpires('');
        setErr(e?.message || 'Could not mint edge token');
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="mt-4 pt-3 border-t border-slate-800 space-y-2">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">MCP edge (product path)</div>
      <p className="text-[10px] text-slate-500 leading-relaxed">
        Deploy the Worker in <code className="text-cyan-200/80">workers/mcp-edge</code>. It verifies a short-lived Bearer and injects{' '}
        <code className="text-cyan-200/80">X-API-KEY</code> to your Render MCP. Set{' '}
        <code className="text-cyan-200/80">REACT_APP_MCP_EDGE_BASE</code> on the dashboard build to show worker URLs below.
      </p>
      {edgeStream ? <CopyField label="MCP endpoint URL (Worker — n8n HTTP Streamable)" value={edgeStream} /> : null}
      {edgeCat ? <CopyField label="Tool catalog (Worker)" value={edgeCat} /> : null}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={generate}
          disabled={loading}
          className="text-[10px] px-3 py-1.5 rounded bg-violet-700 hover:bg-violet-600 disabled:opacity-50 font-semibold"
        >
          {loading ? 'Minting…' : 'Generate edge bearer'}
        </button>
        <span className="text-[10px] text-slate-500">
          Uses your dashboard login JWT. Set MCP_EDGE_BEARER_SECRET on the MCP host (and engine if you mint there); must match the Worker secret.
        </span>
      </div>
      {err ? <div className="text-[10px] text-rose-400 break-words">{err}</div> : null}
      {bearer ? <CopyField label="Bearer token (n8n → Authentication → Bearer Auth)" value={bearer} /> : null}
      {expires ? (
        <div className="text-[10px] text-slate-500">
          Expires <span className="text-slate-300">{expires}</span> — regenerate in n8n before expiry.
        </div>
      ) : null}
    </div>
  );
}

function CopyField({ label, value }) {
  const [ok, setOk] = useState(false);
  const copy = () => {
    if (!value) {
      return;
    }
    void navigator.clipboard.writeText(value).then(() => {
      setOk(true);
      setTimeout(() => setOk(false), 1600);
    });
  };
  return (
    <div className="space-y-1">
      {label ? (
        <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      ) : null}
      <div className="flex gap-2 items-start">
        <code className="flex-1 text-[11px] break-all bg-slate-950 border border-slate-800 rounded p-2 text-cyan-200/90">
          {value || '—'}
        </code>
        <button
          type="button"
          onClick={copy}
          className="shrink-0 text-[10px] px-2 py-1.5 rounded border border-slate-600 hover:bg-slate-800 text-slate-300"
        >
          {ok ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

const BASE_SCOPES = [
  'accounts:list',
  'accounts:read:*',
  'accounts:read:balances',
  'accounts:read:positions',
  'accounts:read:transactions',
];
const TX_SCOPES = ['execution:route', 'execution:trade:*'];

function slugify(s) {
  return (
    String(s || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'provider'
  );
}

function newAccountId(prefix) {
  const p = slugify(prefix);
  return `acc-${p}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

function readPathname() {
  return typeof window !== 'undefined' ? window.location.pathname.replace(/\/$/, '') || '/' : '/';
}

function Modal({ title, children, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="hub-modal-title"
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-slate-700 bg-slate-900 p-5 shadow-2xl">
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 id="hub-modal-title" className="text-sm font-bold uppercase tracking-wide text-slate-200">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-slate-600 px-2 py-0.5 text-xs text-slate-300 hover:bg-slate-800"
          >
            Close
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function BinanceMark() {
  return (
    <img
      src={`${process.env.PUBLIC_URL}/binance-tile.svg`}
      alt=""
      width={56}
      height={56}
      className="h-14 w-14 rounded-lg border border-amber-700/50 object-cover"
    />
  );
}

export default function AccountHubDashboard() {
  const [accounts, setAccounts] = useState([]);
  const [apps, setApps] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [walletAssignments, setWalletAssignments] = useState([]);
  const [audit, setAudit] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newAppName, setNewAppName] = useState('');
  const [newAppKind, setNewAppKind] = useState('llm');
  const [integrationCreated, setIntegrationCreated] = useState(null);
  const [removingAppId, setRemovingAppId] = useState('');

  const [binanceOpen, setBinanceOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);

  const [bnApiKey, setBnApiKey] = useState('');
  const [bnApiSecret, setBnApiSecret] = useState('');
  const [bnDisplayName, setBnDisplayName] = useState('');
  const [bnKeyAlias, setBnKeyAlias] = useState('binance-primary');
  const [bnConnecting, setBnConnecting] = useState(false);
  const [bnVerifyMsg, setBnVerifyMsg] = useState('');

  const [cuDisplayName, setCuDisplayName] = useState('');
  const [cuProvider, setCuProvider] = useState('');
  const [cuAccountType, setCuAccountType] = useState('crypto_exchange');
  const [cuCurrency, setCuCurrency] = useState('');
  const [walletName, setWalletName] = useState('');
  const [walletProvider, setWalletProvider] = useState('metamask');
  /** Selected wallet tile id from WALLET_TILES, or "manual" for the form-only path */
  const [walletPickerId, setWalletPickerId] = useState('metamask');
  const [walletConnectionType, setWalletConnectionType] = useState('injected');
  const [walletAddress, setWalletAddress] = useState('');
  const [walletScopeRead, setWalletScopeRead] = useState(true);
  const [walletScopeTransfer, setWalletScopeTransfer] = useState(false);
  const [walletScopeTrade, setWalletScopeTrade] = useState(false);
  const [walletSignerType, setWalletSignerType] = useState('');
  const [walletSignerRef, setWalletSignerRef] = useState('');
  /** Last successful injected EVM session (MetaMask, Brave, Rabby, …) */
  const [injectedSession, setInjectedSession] = useState(null);
  const [injectedConnecting, setInjectedConnecting] = useState('');
  const [wcSession, setWcSession] = useState(null);
  const [wcConnecting, setWcConnecting] = useState(false);
  const wcProviderRef = useRef(null);
  const [phantomSolPublicKey, setPhantomSolPublicKey] = useState('');
  const [phantomEvmAddress, setPhantomEvmAddress] = useState('');
  const [phantomEvmChainHex, setPhantomEvmChainHex] = useState('');
  const [phantomConnecting, setPhantomConnecting] = useState('');
  const [walletTags, setWalletTags] = useState('');
  const [walletBalances, setWalletBalances] = useState({});
  const [agentId, setAgentId] = useState('');
  const [selectedAppId, setSelectedAppId] = useState('');
  const [assignmentRole, setAssignmentRole] = useState('execution');
  const [assignmentScope, setAssignmentScope] = useState('read_only');
  const [assignmentSpendLimit, setAssignmentSpendLimit] = useState('');
  const [assignmentAllowedContracts, setAssignmentAllowedContracts] = useState('');
  const [assignmentAllowedTokens, setAssignmentAllowedTokens] = useState('');
  const [assignmentAllowedChains, setAssignmentAllowedChains] = useState('');
  const [assignmentExpiresAt, setAssignmentExpiresAt] = useState('');
  const [assignmentCanRead, setAssignmentCanRead] = useState(true);
  const [assignmentCanSign, setAssignmentCanSign] = useState(false);
  const [assignmentCanTransact, setAssignmentCanTransact] = useState(false);
  const [editingWalletId, setEditingWalletId] = useState('');
  const [editWalletChain, setEditWalletChain] = useState('universal');
  const [editWalletType, setEditWalletType] = useState('non_custodial');
  const [editWalletMode, setEditWalletMode] = useState('watch_only');
  const [editWalletTags, setEditWalletTags] = useState('');
  const [editWalletScopes, setEditWalletScopes] = useState('wallet:read');
  const [editWalletSignerType, setEditWalletSignerType] = useState('');
  const [editWalletSignerRef, setEditWalletSignerRef] = useState('');
  const [removingWalletId, setRemovingWalletId] = useState('');
  const [removingAssignmentId, setRemovingAssignmentId] = useState('');

  const path = readPathname();

  const isAccountsView = path.includes('/account-hub/accounts');
  const isWalletsView = path.includes('/account-hub/wallets');
  const isAgentsView = path.includes('/account-hub/agents');
  const isAppsView =
    path.includes('/account-hub') && !isAccountsView && !isWalletsView && !isAgentsView;
  const metamaskInstalled = typeof window !== 'undefined' && Boolean(getMetaMaskProvider());
  const phantomSolInstalled = typeof window !== 'undefined' && Boolean(getPhantomSolana());
  const phantomEvmInstalled = typeof window !== 'undefined' && Boolean(getPhantomEthereum());

  const activeWalletTile = useMemo(
    () => (walletPickerId !== 'manual' ? getWalletTile(walletPickerId) : null),
    [walletPickerId]
  );

  const refresh = async () => {
    try {
      setLoading(true);
      setError('');
      const [accRes, appRes, auditRes, walletRes, assignmentRes, agentRes] = await Promise.all([
        mcpGet('/v1/account-hub/accounts'),
        mcpGet('/v1/account-hub/apps'),
        mcpGet('/v1/account-hub/audit?limit=20'),
        mcpGet('/v1/account-hub/wallets'),
        mcpGet('/v1/account-hub/wallet-assignments'),
        mcpGet('/v1/account-hub/agents').catch(() => ({ agents: [] })),
      ]);
      setAccounts(accRes.accounts || []);
      setApps(appRes.apps || []);
      setAudit(auditRes.events || []);
      setWallets(walletRes.wallets || []);
      setWalletAssignments(assignmentRes.assignments || []);
      setAgents(agentRes.agents || []);
    } catch (err) {
      setError(err.message || 'Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const p = readPathname();
    if (p === '/account-hub') {
      window.location.replace('/account-hub/apps');
      return;
    }
    refresh();
  }, []);

  useEffect(() => {
    if (!injectedSession?.providerId) {
      return undefined;
    }
    const pid = injectedSession.providerId;
    const eth = resolveInjectedProvider(pid);
    if (!eth || typeof eth.on !== 'function') {
      return undefined;
    }
    const onAccountsChanged = (accounts) => {
      const first = Array.isArray(accounts) && accounts.length > 0 ? accounts[0] : '';
      setInjectedSession((prev) =>
        prev && prev.providerId === pid ? { ...prev, address: first } : prev
      );
      if (first && walletProvider === pid) {
        setWalletAddress(first);
      }
    };
    const onChainChanged = (chainId) => {
      const hex = String(chainId || '').toLowerCase();
      setInjectedSession((prev) =>
        prev && prev.providerId === pid ? { ...prev, chainHex: hex } : prev
      );
    };
    eth.on('accountsChanged', onAccountsChanged);
    eth.on('chainChanged', onChainChanged);
    return () => {
      if (eth.removeListener) {
        eth.removeListener('accountsChanged', onAccountsChanged);
        eth.removeListener('chainChanged', onChainChanged);
      }
    };
  }, [injectedSession?.providerId, walletProvider]);

  useEffect(() => {
    if (walletProvider !== 'phantom') {
      return undefined;
    }
    const sol = getPhantomSolana();
    if (!sol) {
      return undefined;
    }
    void sol
      .connect({ onlyIfTrusted: true })
      .then((resp) => {
        const pk = resp?.publicKey || sol.publicKey;
        const s = pk?.toString?.() || '';
        if (s) {
          setPhantomSolPublicKey(s);
        }
      })
      .catch(() => {});
    return undefined;
  }, [walletProvider]);

  useEffect(() => {
    const sol = getPhantomSolana();
    if (!sol || typeof sol.on !== 'function') {
      return undefined;
    }
    const onAccountChanged = (publicKey) => {
      if (publicKey) {
        const s = publicKey.toBase58?.() || publicKey.toString?.() || '';
        setPhantomSolPublicKey(s);
        if (walletProvider === 'phantom') {
          setWalletAddress(s);
        }
      } else {
        setPhantomSolPublicKey('');
        if (walletProvider === 'phantom') {
          setWalletAddress('');
        }
      }
    };
    const onDisconnect = () => {
      setPhantomSolPublicKey('');
      if (walletProvider === 'phantom') {
        setWalletAddress('');
      }
    };
    sol.on('accountChanged', onAccountChanged);
    sol.on('disconnect', onDisconnect);
    return () => {
      if (sol.removeListener) {
        sol.removeListener('accountChanged', onAccountChanged);
        sol.removeListener('disconnect', onDisconnect);
      }
    };
  }, [walletProvider]);

  useEffect(() => {
    const eth = getPhantomEthereum();
    if (!eth || typeof eth.on !== 'function') {
      return undefined;
    }
    const onAccountsChanged = (accounts) => {
      const first = Array.isArray(accounts) && accounts.length > 0 ? accounts[0] : '';
      setPhantomEvmAddress(first);
      if (first && walletProvider === 'phantom') {
        setWalletAddress(first);
      }
      if (!first) {
        setPhantomEvmChainHex('');
      }
    };
    const onChainChanged = (chainId) => {
      setPhantomEvmChainHex(String(chainId || '').toLowerCase());
    };
    eth.on('accountsChanged', onAccountsChanged);
    eth.on('chainChanged', onChainChanged);
    return () => {
      if (eth.removeListener) {
        eth.removeListener('accountsChanged', onAccountsChanged);
        eth.removeListener('chainChanged', onChainChanged);
      }
    };
  }, [walletProvider]);

  useEffect(() => {
    let cancelled = false;
    if (wallets.length === 0) {
      setWalletBalances({});
      return undefined;
    }

    const refreshBalances = async () => {
      const entries = await Promise.all(
        wallets.map(async (w) => {
          const walletId = w.wallet_id;
          const address = String(w.address || '').trim();
          const chain = String(w.chain || '').toLowerCase();

          if (!walletId || isPendingAddress(address)) {
            return [walletId, { status: 'unavailable', text: 'Not connected yet' }];
          }

          try {
            if (chain === 'solana') {
              const lamports = await jsonRpcPost(SOLANA_MAINNET_RPC, 'getBalance', [address]).then(
                (r) => r?.value
              );
              if (typeof lamports === 'number') {
                const amount = (lamports / 1e9).toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
                return [walletId, { status: 'ok', text: `${amount} SOL` }];
              }
              return [walletId, { status: 'unavailable', text: 'Balance unavailable' }];
            }

            const rpcChain = chain === 'universal' ? 'ethereum' : chain;
            const rpcUrl = EVM_PUBLIC_RPC[rpcChain];
            if (rpcUrl) {
              try {
                const raw = await jsonRpcPost(rpcUrl, 'eth_getBalance', [address, 'latest']);
                const value = formatNativeAmount(raw, 18, 6);
                if (value) {
                  return [walletId, { status: 'ok', text: `${value} ${nativeSymbolForChain(rpcChain)}` }];
                }
              } catch (_) {
                // Fallback to active wallet provider below.
              }
            }

            const evmProvider =
              (w.connection_type === 'walletconnect' &&
                wcSession?.providerId === w.provider &&
                wcProviderRef.current) ||
              (w.provider === 'phantom' ? getPhantomEthereum() : resolveInjectedProvider(w.provider));

            if (!evmProvider?.request) {
              return [walletId, { status: 'unavailable', text: 'Open wallet to read balance' }];
            }

            const raw = await evmProvider.request({
              method: 'eth_getBalance',
              params: [address, 'latest'],
            });
            const value = formatNativeAmount(raw, 18, 6);
            if (!value) {
              return [walletId, { status: 'unavailable', text: 'Balance unavailable' }];
            }
            return [walletId, { status: 'ok', text: `${value} ${nativeSymbolForChain(chain)}` }];
          } catch (_) {
            return [walletId, { status: 'error', text: 'Balance error' }];
          }
        })
      );

      if (!cancelled) {
        setWalletBalances(Object.fromEntries(entries.filter(([id]) => id)));
      }
    };

    void refreshBalances();
    const t = window.setInterval(() => {
      void refreshBalances();
    }, 20000);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, [wallets, wcSession, injectedSession, phantomEvmAddress, phantomSolPublicKey]);

  const disconnectWalletConnectSession = async () => {
    const p = wcProviderRef.current;
    wcProviderRef.current = null;
    if (p && typeof p.disconnect === 'function') {
      try {
        await p.disconnect();
      } catch (_) {
        /* ignore */
      }
    }
  };

  const ensureWalletName = () => {
    if (walletName.trim()) {
      return true;
    }
    setError('Enter wallet name first');
    return false;
  };

  const connectInjectedById = async (providerId, displayLabel) => {
    if (!ensureWalletName()) {
      return;
    }
    const eth = resolveInjectedProvider(providerId);
    if (!eth) {
      setError(`${displayLabel} is not available in this browser`);
      return;
    }
    try {
      setError('');
      setInjectedConnecting(providerId);
      const accounts = await eth.request({ method: 'eth_requestAccounts' });
      const account = Array.isArray(accounts) && accounts.length > 0 ? accounts[0] : '';
      const chainHexRaw = await eth.request({ method: 'eth_chainId' });
      const chainHex = String(chainHexRaw || '').toLowerCase();
      setInjectedSession({ providerId, address: account, chainHex });
      setWalletProvider(providerId);
      setWalletConnectionType('injected');
      setWalletSignerType('injected');
      setWalletSignerRef(`${providerId}:${account || 'session'}`);
      setWalletAddress(account);
      setPhantomSolPublicKey('');
      setPhantomEvmAddress('');
      setPhantomEvmChainHex('');
      setWcSession(null);
      await disconnectWalletConnectSession();
    } catch (err) {
      setError(err?.message || `Failed to connect ${displayLabel}`);
    } finally {
      setInjectedConnecting('');
    }
  };

  const connectMetaMask = async () => {
    await connectInjectedById('metamask', 'MetaMask');
  };

  const runWalletConnect = async () => {
    if (!ensureWalletName()) {
      return;
    }
    const tile = getWalletTile(walletPickerId);
    if (!tile || tile.panel !== 'wc') {
      return;
    }
    try {
      setError('');
      setWcConnecting(true);
      await disconnectWalletConnectSession();
      const { provider, address, chainHex } = await connectWalletConnectEvm();
      wcProviderRef.current = provider;
      setWcSession({ providerId: tile.provider, address, chainHex });
      setWalletProvider(tile.provider);
      setWalletConnectionType('walletconnect');
      setWalletSignerType('walletconnect');
      setWalletSignerRef(`walletconnect:${tile.provider}:${address || 'session'}`);
      setWalletAddress(address);
      setInjectedSession(null);
      setPhantomSolPublicKey('');
      setPhantomEvmAddress('');
      setPhantomEvmChainHex('');
    } catch (err) {
      setError(err?.message || 'WalletConnect failed');
    } finally {
      setWcConnecting(false);
    }
  };

  const connectPhantomSolana = async () => {
    if (!ensureWalletName()) {
      return;
    }
    const sol = getPhantomSolana();
    if (!sol) {
      setError('Phantom is not available here. Use HTTPS or localhost, or open in Phantom mobile.');
      return;
    }
    try {
      setError('');
      setPhantomConnecting('solana');
      setPhantomEvmAddress('');
      setPhantomEvmChainHex('');
      const resp = await sol.connect();
      const pk = resp?.publicKey || sol.publicKey;
      const address = pk?.toString?.() || '';
      setPhantomSolPublicKey(address);
      setWalletProvider('phantom');
      setWalletConnectionType('injected');
      setWalletSignerType('injected');
      setWalletSignerRef(`phantom:solana:${address || 'session'}`);
      setWalletAddress(address);
      setInjectedSession(null);
      setWcSession(null);
      await disconnectWalletConnectSession();
    } catch (err) {
      setError(err?.message || 'Failed to connect Phantom (Solana)');
    } finally {
      setPhantomConnecting('');
    }
  };

  const connectPhantomEVM = async () => {
    if (!ensureWalletName()) {
      return;
    }
    const eth = getPhantomEthereum();
    if (!eth) {
      setError('Phantom EVM provider not found. Install Phantom or use the Solana connect action.');
      return;
    }
    try {
      setError('');
      setPhantomConnecting('evm');
      setPhantomSolPublicKey('');
      const accounts = await eth.request({ method: 'eth_requestAccounts' });
      const account = Array.isArray(accounts) && accounts.length > 0 ? accounts[0] : '';
      const chainHexRaw = await eth.request({ method: 'eth_chainId' });
      const chainHex = String(chainHexRaw || '').toLowerCase();
      setPhantomEvmAddress(account);
      setPhantomEvmChainHex(chainHex);
      setWalletProvider('phantom');
      setWalletConnectionType('injected');
      setWalletSignerType('injected');
      setWalletSignerRef(`phantom:evm:${account || 'session'}`);
      setWalletAddress(account);
      setInjectedSession(null);
      setWcSession(null);
      await disconnectWalletConnectSession();
    } catch (err) {
      setError(err?.message || 'Failed to connect Phantom (EVM)');
    } finally {
      setPhantomConnecting('');
    }
  };

  const createApp = async () => {
    if (!newAppName.trim()) return;
    try {
      const res = await mcpPost('/v1/account-hub/apps', {
        name: newAppName.trim(),
        kind: newAppKind,
      });
      const app = res.app || {};
      setIntegrationCreated({
        name: app.name || newAppName.trim(),
        kind: app.kind || newAppKind,
        app_id: app.app_id || '',
        app_token: res.app_token || '',
      });
      setNewAppName('');
      await refresh();
    } catch (err) {
      setError(err.message || 'Failed to create app');
    }
  };

  const toggleTransactional = async (appId, enabled) => {
    try {
      await mcpPost('/v1/account-hub/grants', {
        app_id: appId,
        scopes: enabled ? [...BASE_SCOPES, ...TX_SCOPES] : BASE_SCOPES,
        transactional_access: enabled,
      });
      await refresh();
    } catch (err) {
      setError(err.message || 'Failed to update grant');
    }
  };

  const removeIntegration = async (app) => {
    if (!app?.app_id) {
      return;
    }
    const ok = window.confirm(`Remove integration "${app.name}"? This revokes its token and grants.`);
    if (!ok) {
      return;
    }
    try {
      setRemovingAppId(app.app_id);
      await mcpDelete(`/v1/account-hub/apps?app_id=${encodeURIComponent(app.app_id)}`);
      await refresh();
    } catch (err) {
      setError(err.message || 'Failed to remove integration');
    } finally {
      setRemovingAppId('');
    }
  };

  const capabilitiesForType = useMemo(
    () => ({
      crypto_exchange: ['balances', 'orders', 'positions', 'transactions'],
      brokerage: ['balances', 'positions', 'transactions'],
      bank: ['balances', 'transactions'],
      wallet: ['balances', 'transactions'],
    }),
    []
  );

  const submitBinance = async () => {
    const displayName = bnDisplayName.trim() || 'Binance';
    const keyAlias = (bnKeyAlias.trim() || 'binance-primary').replace(/\s+/g, '-');
    const accountId = newAccountId('binance');
    if (!bnApiKey.trim() || !bnApiSecret.trim()) {
      setError('Binance API key and secret are required');
      return;
    }
    setBnConnecting(true);
    setBnVerifyMsg('');
    setError('');
    try {
      await mcpPost('/v1/account-hub/connectors/credentials', {
        provider: 'binance',
        key_alias: keyAlias,
        payload: {
          api_key: bnApiKey.trim(),
          api_secret: bnApiSecret.trim(),
        },
      });
      await mcpPost('/v1/account-hub/accounts', {
        account_id: accountId,
        provider: 'binance',
        account_type: 'crypto_exchange',
        display_name: displayName,
        currency: 'USDT',
        read_only_supported: true,
        transaction_supported: true,
        capabilities: capabilitiesForType.crypto_exchange,
        metadata: {
          key_alias: keyAlias,
          integration: 'binance',
          account_kind: 'spot',
        },
      });
      const balRes = await mcpGet('/v1/account-hub/balances?provider=binance');
      const count = Array.isArray(balRes.balances) ? balRes.balances.length : 0;
      setBnVerifyMsg(
        count > 0
          ? `Verified: ${count} balance row(s) from Binance.`
          : 'Connected, but Binance returned no non-zero balances (check API permissions).'
      );
      await refresh();
      setBnApiKey('');
      setBnApiSecret('');
    } catch (err) {
      setError(err.message || 'Failed to connect Binance');
      setBnVerifyMsg('');
    } finally {
      setBnConnecting(false);
    }
  };

  const closeBinanceModal = () => {
    setBinanceOpen(false);
    setBnDisplayName('');
    setBnKeyAlias('binance-primary');
    setBnVerifyMsg('');
    setBnApiKey('');
    setBnApiSecret('');
  };

  const submitCustom = async () => {
    const name = cuDisplayName.trim();
    const prov = slugify(cuProvider);
    if (!name || !cuProvider.trim()) {
      setError('Display name and provider id are required');
      return;
    }
    const caps = capabilitiesForType[cuAccountType] || capabilitiesForType.bank;
    const currency = cuCurrency.trim() || (cuAccountType === 'crypto_exchange' ? 'USDT' : 'USD');
    try {
      await mcpPost('/v1/account-hub/accounts', {
        account_id: newAccountId(prov),
        provider: prov,
        account_type: cuAccountType,
        display_name: name,
        currency,
        read_only_supported: true,
        transaction_supported: false,
        capabilities: caps,
        metadata: {
          connection: 'custom',
        },
      });
      setCustomOpen(false);
      setCuDisplayName('');
      setCuProvider('');
      setCuAccountType('crypto_exchange');
      setCuCurrency('');
      await refresh();
    } catch (err) {
      setError(err.message || 'Failed to add custom connection');
    }
  };

  const connectWallet = async () => {
    if (!walletName.trim()) {
      setError('Wallet name is required');
      return;
    }
    const phantomResolvedAddr =
      walletProvider === 'phantom' ? phantomEvmAddress || phantomSolPublicKey : '';
    const injectedResolvedAddr =
      injectedSession && injectedSession.providerId === walletProvider
        ? injectedSession.address
        : '';
    const wcResolvedAddr =
      wcSession && wcSession.providerId === walletProvider ? wcSession.address : '';
    const resolvedAddress =
      walletAddress.trim() ||
      phantomResolvedAddr ||
      injectedResolvedAddr ||
      wcResolvedAddr ||
      `pending_${Date.now().toString(36)}`;
    let resolvedChain = 'universal';
    if (
      injectedSession &&
      injectedSession.providerId === walletProvider &&
      injectedSession.chainHex
    ) {
      resolvedChain = EVM_HEX_TO_CHAIN[injectedSession.chainHex] || 'universal';
    } else if (wcSession && wcSession.providerId === walletProvider && wcSession.chainHex) {
      resolvedChain = EVM_HEX_TO_CHAIN[wcSession.chainHex] || 'universal';
    } else if (walletProvider === 'phantom') {
      if (phantomEvmChainHex) {
        resolvedChain = EVM_HEX_TO_CHAIN[phantomEvmChainHex] || 'universal';
      } else if (phantomSolPublicKey) {
        resolvedChain = 'solana';
      }
    }
    const scopes = [];
    if (walletScopeRead) scopes.push('wallet:read');
    if (walletScopeTransfer) scopes.push('wallet:transfer');
    if (walletScopeTrade) scopes.push('wallet:trade');
    try {
      setError('');
      await mcpPost('/v1/account-hub/wallets', {
        display_name: walletName.trim(),
        provider: walletProvider,
        connection_type: walletConnectionType,
        chain: resolvedChain,
        address: resolvedAddress,
        wallet_type: 'non_custodial',
        mode: 'watch_only',
        scopes: scopes.length ? scopes : ['wallet:read'],
        signer_type: walletSignerType.trim(),
        signer_ref: walletSignerRef.trim(),
        status: 'connected',
        capabilities: ['balances'],
        tags: walletTags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
      });
      setWalletName('');
      setWalletAddress('');
      setWalletTags('');
      setWalletConnectionType(
        walletPickerId === 'manual'
          ? 'manual'
          : getWalletTile(walletPickerId)?.connection || 'injected'
      );
      setWalletScopeRead(true);
      setWalletScopeTransfer(false);
      setWalletScopeTrade(false);
      setWalletSignerType('');
      setWalletSignerRef('');
      setInjectedSession(null);
      setWcSession(null);
      await disconnectWalletConnectSession();
      setPhantomSolPublicKey('');
      setPhantomEvmAddress('');
      setPhantomEvmChainHex('');
      await refresh();
    } catch (err) {
      setError(err.message || 'Failed to connect wallet');
    }
  };

  const beginEditWallet = (wallet) => {
    if (!wallet) return;
    setEditingWalletId(wallet.wallet_id);
    setEditWalletChain(wallet.chain || 'universal');
    setEditWalletType(wallet.wallet_type || 'non_custodial');
    setEditWalletMode(wallet.mode || 'watch_only');
    setEditWalletTags((wallet.tags || []).join(', '));
    setEditWalletScopes((wallet.scopes || ['wallet:read']).join(', '));
    setEditWalletSignerType(wallet.signer_type || '');
    setEditWalletSignerRef(wallet.signer_ref || '');
  };

  const saveWalletSettings = async () => {
    const wallet = wallets.find((w) => w.wallet_id === editingWalletId);
    if (!wallet) {
      setError('Pick a wallet to edit');
      return;
    }
    try {
      setError('');
      await mcpPost('/v1/account-hub/wallets', {
        wallet_id: wallet.wallet_id,
        display_name: wallet.display_name,
        provider: wallet.provider,
        connection_type: wallet.connection_type || 'manual',
        chain: editWalletChain,
        address: wallet.address,
        wallet_type: editWalletType,
        mode: editWalletMode,
        scopes: editWalletScopes
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean),
        signer_type: editWalletSignerType,
        signer_ref: editWalletSignerRef,
        status: wallet.status || 'connected',
        capabilities: wallet.capabilities || ['balances'],
        tags: editWalletTags
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean),
      });
      await refresh();
    } catch (err) {
      setError(err.message || 'Failed to update wallet settings');
    }
  };

  const assignWallet = async (walletId) => {
    if (!walletId) {
      return;
    }
    if (!selectedAppId && !agentId.trim()) {
      setError('Pick an app integration or provide an agent id');
      return;
    }
    try {
      setError('');
      await mcpPost('/v1/account-hub/wallet-assignments', {
        wallet_id: walletId,
        app_id: selectedAppId || '',
        agent_id: agentId.trim(),
        role: assignmentRole,
        scope: assignmentScope,
        can_read: assignmentCanRead,
        can_sign: assignmentCanSign,
        can_transact: assignmentCanTransact,
        spend_limit_usd: assignmentSpendLimit ? Number(assignmentSpendLimit) : 0,
        allowed_contracts: assignmentAllowedContracts
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean),
        allowed_tokens: assignmentAllowedTokens
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean),
        allowed_chains: assignmentAllowedChains
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean),
        expires_at: assignmentExpiresAt ? new Date(assignmentExpiresAt).toISOString() : '',
      });
      await refresh();
    } catch (err) {
      setError(err.message || 'Failed to assign wallet');
    }
  };

  const removeWallet = async (wallet) => {
    if (!wallet?.wallet_id) {
      return;
    }
    if (!window.confirm(`Remove wallet "${wallet.display_name}"?`)) {
      return;
    }
    try {
      setRemovingWalletId(wallet.wallet_id);
      await mcpDelete(`/v1/account-hub/wallets?wallet_id=${encodeURIComponent(wallet.wallet_id)}`);
      await refresh();
    } catch (err) {
      setError(err.message || 'Failed to remove wallet');
    } finally {
      setRemovingWalletId('');
    }
  };

  const removeWalletAssignment = async (assignment) => {
    if (!assignment?.assignment_id) {
      return;
    }
    try {
      setRemovingAssignmentId(assignment.assignment_id);
      await mcpDelete(
        `/v1/account-hub/wallet-assignments?assignment_id=${encodeURIComponent(assignment.assignment_id)}`
      );
      await refresh();
    } catch (err) {
      setError(err.message || 'Failed to remove wallet assignment');
    } finally {
      setRemovingAssignmentId('');
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-6 font-mono">
      <div className="max-w-7xl mx-auto space-y-6">
        <header className="relative flex flex-col gap-4 rounded-3xl border border-white/5 bg-gradient-to-br from-slate-900 via-slate-900/95 to-slate-950 p-5 shadow-xl">
          {/* Clip glows only — overflow-hidden on the whole header clipped the profile dropdown (DashboardNav). */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 overflow-hidden rounded-3xl"
          >
            <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-violet-500/15 blur-[120px]" />
            <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-cyan-500/15 blur-[120px]" />
          </div>
          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 border-b border-white/5 pb-4">
            <DashboardBrandRow />
            <DashboardNav />
          </div>
          <div className="relative z-10 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-violet-300/80 font-semibold">
                {isAgentsView
                  ? 'Agent control plane'
                  : isWalletsView
                    ? 'Wallet vault'
                    : isAccountsView
                      ? 'Account hub'
                      : 'App integrations'}
              </p>
              <h2 className="text-2xl md:text-3xl font-black mt-1 bg-gradient-to-r from-violet-200 via-fuchsia-200 to-cyan-200 bg-clip-text text-transparent">
                {isAgentsView
                  ? 'Agents'
                  : isAccountsView
                    ? 'Linked accounts'
                    : isWalletsView
                      ? 'Wallet integrations'
                      : 'App integrations'}
              </h2>
              <p className="text-xs md:text-sm text-slate-400 mt-2 max-w-2xl">
                {isAgentsView
                  ? 'Every agent that calls our MCP is a registered app with its own token. Track activity, scope wallets to specific agents, and revoke instantly.'
                  : isWalletsView
                    ? 'Connect multiple wallets and map them to app integrations or agent ids for future autonomous routing.'
                    : isAccountsView
                      ? 'Connect exchanges and brokers for MCP tools; transactional access stays per-app.'
                      : 'Register each LLM or client as its own integration. You get a unique id and token; the MCP process can stay the same.'}
              </p>
            </div>
            {isWalletsView ? (
              <div className="flex flex-wrap gap-2 text-[10px] uppercase tracking-[0.25em] font-semibold">
                <span className="rounded-full border border-violet-400/30 bg-violet-500/10 text-violet-200 px-3 py-1">
                  {wallets.length} wallets
                </span>
                <span className="rounded-full border border-emerald-400/30 bg-emerald-500/10 text-emerald-200 px-3 py-1">
                  {walletAssignments.length} assignments
                </span>
              </div>
            ) : null}
            {isAgentsView ? (
              <div className="flex flex-wrap gap-2 text-[10px] uppercase tracking-[0.25em] font-semibold">
                <span className="rounded-full border border-violet-400/30 bg-violet-500/10 text-violet-200 px-3 py-1">
                  {agents.length} agents
                </span>
                <span className="rounded-full border border-emerald-400/30 bg-emerald-500/10 text-emerald-200 px-3 py-1">
                  {walletAssignments.length} wallet links
                </span>
              </div>
            ) : null}
          </div>
        </header>

        {error && (
          <div className="rounded-xl border border-red-900 bg-red-950/30 p-3 text-xs text-red-200">{error}</div>
        )}

        {isAppsView && (
          <>
            <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4 text-[11px] text-slate-400 space-y-2 leading-relaxed">
              <p className="text-xs font-semibold text-slate-200">Same MCP server vs new registration</p>
              <p>
                <span className="text-slate-300">Yes — you can use the same MCP entry</span> (same stdio command or same
                hosted base URL) for every LLM. What must be <strong className="text-slate-200">unique per registration</strong>{' '}
                is the <strong className="text-slate-200">app id</strong> and <strong className="text-slate-200">secret token</strong>{' '}
                below: they separate grants, audit, and <code className="text-cyan-200/90">app_token</code> on execution tools.
              </p>
              <p>
                Add <strong className="text-slate-200">one registration per client</strong> (e.g. “Work Claude”, “Home
                Cursor”) even if the command is identical—each gets its own token. If you expose a public HTTP MCP URL, you can
                set <code className="text-cyan-200/90">REACT_APP_MCP_CONNECT_URL_TEMPLATE</code> with{' '}
                <code className="text-cyan-200/90">{'{{appId}}'}</code> to show a copyable link per app.
              </p>
              <p>
                <span className="text-slate-300">ChatGPT-style “Custom MCP” (URL like </span>
                <code className="text-cyan-200/90">https://…/sse</code>
                <span className="text-slate-300">):</span> you normally use{' '}
                <strong className="text-slate-200">one universal HTTPS endpoint</strong> for everyone, and let{' '}
                <strong className="text-slate-200">OAuth</strong> (or API keys) tell you which user or which integration is
                connecting. Alternatively you can issue{' '}
                <strong className="text-slate-200">a different path or query per registration</strong> (e.g.{' '}
                <code className="text-cyan-200/90">/mcp/sse?app_id=…</code>) to route faster—both are valid; identity must still
                be checked on every request. The catalog server also exposes{' '}
                <strong className="text-slate-200">streamable HTTP MCP</strong> at{' '}
                <code className="text-cyan-200/90">/mcp</code> (same port as the tool catalog; override with{' '}
                <code className="text-cyan-200/90">MCP_STREAMABLE_PATH</code>). ChatGPT cannot reach{' '}
                <code className="text-cyan-200/90">127.0.0.1</code> — use a public HTTPS URL (tunnel) and set{' '}
                <code className="text-cyan-200/90">MCP_PUBLIC_BASE_URL</code> plus{' '}
                <code className="text-cyan-200/90">MCP_OAUTH_ENABLE</code> /{' '}
                <code className="text-cyan-200/90">MCP_OAUTH_AUTHORIZATION_SERVERS</code> on the server so OAuth discovery
                returns real auth/token endpoints (not example.com). For “User-defined OAuth client”, create an app at your IdP
                (e.g. Auth0) with ChatGPT’s callback URL and paste the <strong className="text-slate-200">client id</strong>{' '}
                — an empty client id blocks Create.
              </p>
            </div>

            <div className="rounded-xl border border-amber-900/40 bg-amber-950/20 p-4 text-[11px] text-slate-400 space-y-3 leading-relaxed">
              <p className="text-xs font-semibold text-amber-100/90">ChatGPT “New app” (Custom MCP + OAuth) — what goes in each box</p>
              <p className="text-slate-500">
                This is <strong className="text-slate-300">separate</strong> from <strong className="text-slate-300">Register new integration</strong>{' '}
                below: ChatGPT needs an OAuth app at <em>your IdP</em> (Auth0, Okta, Keycloak, …). Our dashboard registration is for{' '}
                <code className="text-cyan-200/80">app_id</code> / <code className="text-cyan-200/80">app_token</code> on this engine.
              </p>
              <dl className="space-y-2.5 border-t border-amber-900/30 pt-3">
                <div>
                  <dt className="text-slate-300 font-medium">MCP Server URL</dt>
                  <dd>
                    Public HTTPS URL to streamable MCP, e.g.{' '}
                    <code className="text-cyan-200/90">{mcpStreamableConnectUrl() || 'https://YOUR-TUNNEL.example/mcp'}</code>
                    . Must be reachable from the internet (not <code className="text-cyan-200/90">127.0.0.1</code>). Same path you set as{' '}
                    <code className="text-cyan-200/90">MCP_STREAMABLE_PATH</code> (default <code className="text-cyan-200/90">/mcp</code>).
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-300 font-medium">Authentication</dt>
                  <dd>OAuth (required for URL-based MCP in ChatGPT).</dd>
                </div>
                <div>
                  <dt className="text-slate-300 font-medium">Registration method → User-Defined OAuth Client</dt>
                  <dd>
                    Use this until you run Dynamic Client Registration (DCR) on your auth server. DCR needs a{' '}
                    <strong className="text-slate-300">Registration URL</strong> in metadata; CIMD needs your server to advertise it.
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-300 font-medium">Callback URL (from ChatGPT)</dt>
                  <dd>
                    Copy the value ChatGPT shows (e.g. <code className="text-cyan-200/90">https://chatgpt.com/connector/oauth/…</code>). In your IdP
                    app, add it exactly under <strong className="text-slate-300">Allowed Callback URLs</strong> / <strong className="text-slate-300">Redirect URIs</strong>.
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-300 font-medium">OAuth Client ID (and optional Client Secret)</dt>
                  <dd>
                    Create an application at your IdP for this connector. Paste <strong className="text-slate-300">Client ID</strong> here —{' '}
                    <strong className="text-amber-200/90">Create stays blocked if this is empty</strong>. Paste <strong className="text-slate-300">Client Secret</strong> if
                    ChatGPT is using a confidential client (common with <code className="text-cyan-200/90">client_secret_basic</code> /{' '}
                    <code className="text-cyan-200/90">client_secret_post</code>).
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-300 font-medium">Token endpoint auth method</dt>
                  <dd>
                    Match what your IdP expects for that application. Auth0 often documents <code className="text-cyan-200/90">client_secret_post</code>; if
                    ChatGPT only offers <code className="text-cyan-200/90">client_secret_basic</code>, create/configure the IdP app to accept that, or use a
                    public client + PKCE if your IdP and ChatGPT support it.
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-300 font-medium">Default scopes / Base scopes</dt>
                  <dd>
                    Start with <code className="text-cyan-200/90">openid</code>, <code className="text-cyan-200/90">profile</code>, <code className="text-cyan-200/90">email</code>{' '}
                    (if you use OIDC). Add your API audience scopes if the access token must call a specific API identifier. Align with{' '}
                    <code className="text-cyan-200/90">MCP_OAUTH_SCOPES_SUPPORTED</code> on the Go server if you list scopes there.
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-300 font-medium">OAuth endpoints (Auth URL, Token URL, Authorization server base, Resource)</dt>
                  <dd>
                    When discovery works, ChatGPT fills these from your IdP (issuer in{' '}
                    <code className="text-cyan-200/90">MCP_OAUTH_AUTHORIZATION_SERVERS</code>). <strong className="text-slate-300">Auth0 manual template:</strong>{' '}
                    issuer <code className="text-cyan-200/90">https://YOUR_TENANT.us.auth0.com</code> → Auth{' '}
                    <code className="text-cyan-200/90">…/authorize</code>, Token <code className="text-cyan-200/90">…/oauth/token</code>. If you still see{' '}
                    <code className="text-cyan-200/90">example.com</code>, the MCP URL is not public, or <code className="text-cyan-200/90">MCP_OAUTH_ENABLE</code> /{' '}
                    <code className="text-cyan-200/90">MCP_PUBLIC_BASE_URL</code> / issuers are wrong — fix tunnel + env first.
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-300 font-medium">OpenID (OIDC) section</dt>
                  <dd>
                    Enabled only if the authorization server advertises OIDC discovery. Auth0 does at{' '}
                    <code className="text-cyan-200/90">https://YOUR_TENANT.us.auth0.com/.well-known/openid-configuration</code>.
                  </dd>
                </div>
              </dl>
            </div>

            <p className="text-xs text-slate-500">
              <span className="text-slate-300 font-semibold">{apps.length}</span> integration{apps.length !== 1 ? 's' : ''}
              <span className="mx-2 text-slate-600">·</span>
              <span className="text-slate-300 font-semibold">{accounts.length}</span> linked account
              {accounts.length !== 1 ? 's' : ''}
            </p>

            <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
              <h2 className="text-sm font-bold uppercase text-slate-300">Register new integration</h2>
              <p className="text-xs text-slate-500 mt-1">
                Name it for your own reference (e.g. LLM or workspace). You will get an app id and token to use in that client.
              </p>
              <div className="mt-3 flex gap-2 flex-wrap items-center">
                <input
                  className="flex-1 min-w-[200px] bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm"
                  placeholder="e.g. Work laptop · Claude"
                  value={newAppName}
                  onChange={(e) => setNewAppName(e.target.value)}
                />
                <select
                  value={newAppKind}
                  onChange={(e) => setNewAppKind(e.target.value)}
                  className="bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm"
                >
                  <option value="llm">LLM</option>
                  <option value="custom_app">Custom app</option>
                </select>
                <button
                  type="button"
                  onClick={createApp}
                  className="bg-cyan-700 hover:bg-cyan-600 text-xs px-4 rounded py-2 font-semibold"
                >
                  Register
                </button>
              </div>
            </div>

            {integrationCreated ? (
              <Modal
                title="Integration registered"
                onClose={() => setIntegrationCreated(null)}
              >
                <p className="text-xs text-amber-200/90 mb-3">
                  Copy the secret token now — it is not shown again. Use the app id and token in your LLM or agent config.
                </p>
                <div className="space-y-3 text-xs">
                  <div>
                    <span className="text-slate-500">Name</span>
                    <div className="text-slate-200 font-medium">
                      {integrationCreated.name}{' '}
                      <span className="text-slate-500 font-normal">({integrationCreated.kind})</span>
                    </div>
                  </div>
                  <CopyField label="App id" value={integrationCreated.app_id} />
                  <CopyField label="Secret token (app_token for tool calls)" value={integrationCreated.app_token} />
                  <p className="text-[10px] text-slate-500 -mt-1">
                    This <code className="text-cyan-200/80">mcpat_…</code> value is <strong className="text-slate-400">not</strong>{' '}
                    the n8n Bearer. For the edge Worker, mint a separate token below.
                  </p>
                  <McpAutomationEdgePanel appId={integrationCreated.app_id} />
                  <CopyField
                    label="Tool catalog (HTTP, shared)"
                    value={mcpCatalogHttpUrl('include_details=false&hierarchy=false')}
                  />
                  {buildConnectUrl(integrationCreated.app_id) ? (
                    <CopyField
                      label="Remote MCP URL (HTTP streamable — paste into URL-based clients)"
                      value={buildConnectUrl(integrationCreated.app_id)}
                    />
                  ) : null}
                  <p className="text-[10px] text-slate-500 pt-1">
                    <strong className="text-slate-400">Hosted MCP</strong>: if your server requires{' '}
                    <code className="text-cyan-200/80">X-API-KEY</code>, configure that in n8n / ChatGPT / Cursor (header or
                    credential), not in the URL — or use <strong className="text-slate-400">Worker + Bearer</strong> (mint token
                    in the section above). <strong className="text-slate-400">stdio</strong>: run{' '}
                    <code className="text-cyan-200/80">mcp_server</code> from your IDE. <strong className="text-slate-400">HTTP</strong>:
                    use the Remote MCP URL. OAuth in front of <code className="text-cyan-200/80">/mcp</code> is your gateway’s
                    job—do not put the app secret in the URL. Pass <code className="text-cyan-200/80">app_token</code> in tool calls
                    (e.g. <code className="text-cyan-200/80">execution.*</code>).
                  </p>
                </div>
              </Modal>
            ) : null}

            <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
              <h2 className="text-sm font-bold uppercase text-slate-300">Your integrations</h2>
              <p className="text-xs text-slate-500 mt-1">Transactional tools require an explicit grant per integration.</p>
              <div className="mt-3 space-y-2">
                {apps.length === 0 ? <p className="text-xs text-slate-500">None yet — register one above.</p> : null}
                {apps.map((app) => (
                  <div
                    key={app.app_id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border border-slate-800 rounded-lg p-3 bg-slate-950"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-100">{app.name}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5 font-mono truncate" title={app.app_id}>
                        {app.kind} · {app.app_id}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => {
                          void navigator.clipboard.writeText(app.app_id);
                        }}
                        className="text-[10px] px-2 py-1 rounded border border-slate-600 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
                      >
                        Copy id
                      </button>
                      <span className="text-[10px] text-slate-500 uppercase">Transactional</span>
                      <button
                        type="button"
                        onClick={() => toggleTransactional(app.app_id, true)}
                        className="text-xs px-2 py-1 rounded bg-emerald-700 hover:bg-emerald-600"
                      >
                        Enable
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleTransactional(app.app_id, false)}
                        className="text-xs px-2 py-1 rounded bg-slate-700 hover:bg-slate-600"
                      >
                        Disable
                      </button>
                      <button
                        type="button"
                        disabled={removingAppId === app.app_id}
                        onClick={() => removeIntegration(app)}
                        className="text-xs px-2 py-1 rounded bg-red-800 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {removingAppId === app.app_id ? 'Removing…' : 'Remove'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
              <h2 className="text-sm font-bold uppercase text-slate-300">Audit Trail</h2>
              <div className="mt-3 space-y-2">
                {audit.length === 0 ? <p className="text-xs text-slate-500">No recent events.</p> : null}
                {audit.map((ev) => (
                  <div key={ev.event_id} className="text-xs bg-slate-950 border border-slate-800 rounded p-2">
                    <span className="text-cyan-300">{ev.action}</span>
                    <span className="text-slate-400">
                      {' '}
                      · {ev.outcome} · {ev.occurred_at}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {isAgentsView && (
          <>
            <AgentsView
              agents={agents}
              wallets={wallets}
              apps={apps}
              loading={loading}
              error={error}
            />
            <AgentsObservabilityTimeline apps={apps} />
          </>
        )}

        {isAccountsView && (
          <>
            <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Link an account</p>
              <p className="text-xs text-slate-400 mt-2 max-w-2xl">
                Exchanges and brokers you connect here feed read-only MCP tools until you allow transactional access per app.
              </p>
              <div className="mt-6 flex flex-wrap gap-4">
                <button
                  type="button"
                  onClick={() => setBinanceOpen(true)}
                  className="flex w-36 flex-col items-center gap-3 rounded-xl border border-slate-700 bg-slate-950 p-4 text-center hover:border-amber-600/50 hover:bg-slate-900"
                >
                  <BinanceMark />
                  <span className="text-xs font-semibold text-slate-200">Binance</span>
                </button>
                <button
                  type="button"
                  onClick={() => setCustomOpen(true)}
                  className="flex w-36 flex-col items-center gap-3 rounded-xl border border-dashed border-slate-600 bg-slate-950 p-4 text-center hover:border-slate-500 hover:bg-slate-900"
                >
                  <div
                    className="flex h-14 w-14 items-center justify-center rounded-lg border border-slate-600 text-2xl font-light text-slate-300"
                    aria-hidden
                  >
                    +
                  </div>
                  <span className="text-xs font-semibold text-slate-200">Custom</span>
                </button>
              </div>
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
              <h2 className="text-sm font-bold uppercase text-slate-300">Connected</h2>
              {loading ? (
                <p className="text-xs text-slate-500 mt-3">Loading...</p>
              ) : accounts.length === 0 ? (
                <p className="text-xs text-slate-500 mt-3">No accounts linked yet.</p>
              ) : (
                <p className="text-xs text-slate-300 mt-3">
                  <span className="text-emerald-400 font-semibold">Connected</span>
                  <span className="text-slate-500">
                    {' '}
                    · {accounts.length} {accounts.length === 1 ? 'account' : 'accounts'} linked
                  </span>
                </p>
              )}
            </div>
          </>
        )}

        {isWalletsView && (
          <>
            <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
              <h2 className="text-sm font-bold uppercase text-slate-300">Connect wallet</h2>
              <p className="text-xs text-slate-500 mt-1">
                Connect first, then configure chain, mode, scopes, signer, tags in edit settings.
              </p>
              <p className="text-[10px] text-slate-600 mt-1 max-w-3xl">
                Extension tiles use standard injected detection; QR tiles use WalletConnect v2 (needs project id in env).
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {WALLET_TILES.map((tile) => (
                  <button
                    key={tile.id}
                    type="button"
                    aria-label={tile.label}
                    aria-pressed={walletPickerId === tile.id}
                    onClick={() => {
                      setWalletPickerId(tile.id);
                      setWalletProvider(tile.provider);
                      setWalletConnectionType(tile.connection);
                    }}
                    className={`flex w-[4.85rem] flex-col items-center gap-1 rounded-lg border p-2 text-center transition-colors ${
                      walletPickerId === tile.id
                        ? 'border-cyan-600/55 bg-slate-800 ring-1 ring-cyan-500/25'
                        : 'border-slate-700 bg-slate-950 hover:border-slate-600 hover:bg-slate-900'
                    }`}
                  >
                    <img src={tile.icon} alt="" className="h-8 w-8 rounded-md object-cover" />
                    <span className="text-[9px] font-semibold text-slate-200 leading-tight">{tile.label}</span>
                  </button>
                ))}
                <button
                  type="button"
                  aria-label="Other wallet, manual entry"
                  aria-pressed={walletPickerId === 'manual'}
                  onClick={() => {
                    setWalletPickerId('manual');
                    setWalletProvider((p) =>
                      WALLET_TILES.some((t) => t.provider === p) ? 'custom' : p
                    );
                    setWalletConnectionType('manual');
                  }}
                  className={`flex w-[4.85rem] flex-col items-center gap-1 rounded-lg border border-dashed p-2 text-center transition-colors ${
                    walletPickerId === 'manual'
                      ? 'border-cyan-600/50 bg-slate-800 ring-1 ring-cyan-500/20'
                      : 'border-slate-600 bg-slate-950 hover:border-slate-500 hover:bg-slate-900'
                  }`}
                >
                  <div
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-slate-600 text-lg font-light text-slate-300"
                    aria-hidden
                  >
                    +
                  </div>
                  <span className="text-[9px] font-semibold text-slate-200 leading-tight">Other</span>
                </button>
              </div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                <input
                  className="bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm md:col-span-2 lg:col-span-3"
                  placeholder="Wallet name"
                  value={walletName}
                  onChange={(e) => setWalletName(e.target.value)}
                />
                {walletPickerId === 'manual' ? (
                  <>
                    <select
                      className="bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm"
                      value={walletProvider}
                      onChange={(e) => setWalletProvider(e.target.value)}
                    >
                      <option value="walletconnect">WalletConnect</option>
                      <option value="metamask">MetaMask</option>
                      <option value="phantom">Phantom</option>
                      <option value="coinbase">Coinbase</option>
                      <option value="brave">Brave</option>
                      <option value="rabby">Rabby</option>
                      <option value="okx">OKX</option>
                      <option value="frame">Frame</option>
                      <option value="trust">Trust</option>
                      <option value="rainbow">Rainbow</option>
                      <option value="bitget">Bitget</option>
                      <option value="tokenpocket">TokenPocket</option>
                      <option value="safe">Safe</option>
                      <option value="custom">Custom</option>
                    </select>
                    <select
                      className="bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm"
                      value={walletConnectionType}
                      onChange={(e) => setWalletConnectionType(e.target.value)}
                    >
                      <option value="manual">Manual profile</option>
                      <option value="injected">Injected provider</option>
                      <option value="walletconnect">WalletConnect / QR</option>
                      <option value="deeplink">Mobile deeplink</option>
                      <option value="custody">Custody/MPC connector</option>
                    </select>
                  </>
                ) : null}
                {activeWalletTile?.panel === 'metamask' ? (
                  <div className="md:col-span-2 lg:col-span-3 rounded border border-orange-700/40 bg-orange-950/20 p-3">
                    <div className="flex flex-wrap items-center gap-3">
                      <img
                        src={`${process.env.PUBLIC_URL}/metamask-fox.svg`}
                        alt="MetaMask"
                        className="h-8 w-8 rounded"
                      />
                      <span className="text-xs text-orange-100/90">Extension or MetaMask mobile.</span>
                      <button
                        type="button"
                        onClick={connectMetaMask}
                        disabled={injectedConnecting === 'metamask' || !walletName.trim()}
                        className="text-xs px-3 py-1.5 rounded bg-orange-700 hover:bg-orange-600 disabled:opacity-60"
                      >
                        {injectedConnecting === 'metamask' ? 'Connecting…' : 'Connect MetaMask'}
                      </button>
                      <a
                        href={metamaskDeepLinkForCurrentDapp() || '#'}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs px-3 py-1.5 rounded border border-orange-600 text-orange-200 hover:bg-orange-900/40"
                      >
                        Open in MetaMask mobile
                      </a>
                    </div>
                    <div className="mt-2 text-[11px] text-slate-300">
                      {injectedSession?.providerId === 'metamask' && injectedSession.address ? (
                        <span>
                          Connected:{' '}
                          <code className="text-cyan-200/90">{injectedSession.address}</code>
                          {injectedSession.chainHex ? (
                            <>
                              {' '}
                              · chain{' '}
                              <code className="text-cyan-200/90">{injectedSession.chainHex}</code>
                            </>
                          ) : null}
                        </span>
                      ) : (
                        <span>
                          {metamaskInstalled
                            ? 'Extension detected.'
                            : 'Extension not detected. Try mobile link or Other.'}
                        </span>
                      )}
                    </div>
                  </div>
                ) : null}
                {activeWalletTile?.panel === 'injected' ? (
                  <div className="md:col-span-2 lg:col-span-3 rounded border border-slate-600/60 bg-slate-950/80 p-3">
                    <div className="flex flex-wrap items-center gap-3">
                      <img src={activeWalletTile.icon} alt="" className="h-8 w-8 rounded" />
                      <span className="text-xs text-slate-300">
                        {isInjectedAvailable(activeWalletTile.provider)
                          ? 'Extension detected.'
                          : 'Extension not detected in this browser.'}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          void connectInjectedById(activeWalletTile.provider, activeWalletTile.label)
                        }
                        disabled={
                          injectedConnecting === activeWalletTile.provider || !walletName.trim()
                        }
                        className="text-xs px-3 py-1.5 rounded bg-slate-600 hover:bg-slate-500 disabled:opacity-60"
                      >
                        {injectedConnecting === activeWalletTile.provider
                          ? 'Connecting…'
                          : `Connect ${activeWalletTile.label}`}
                      </button>
                    </div>
                    <div className="mt-2 text-[11px] text-slate-300">
                      {injectedSession?.providerId === activeWalletTile.provider &&
                      injectedSession.address ? (
                        <span>
                          <code className="text-cyan-200/90">{injectedSession.address}</code>
                          {injectedSession.chainHex ? (
                            <>
                              {' '}
                              · <code className="text-cyan-200/90">{injectedSession.chainHex}</code>
                            </>
                          ) : null}
                        </span>
                      ) : null}
                    </div>
                  </div>
                ) : null}
                {activeWalletTile?.panel === 'wc' ? (
                  <div className="md:col-span-2 lg:col-span-3 rounded border border-sky-700/40 bg-sky-950/20 p-3">
                    <div className="flex flex-wrap items-center gap-3">
                      <img src={activeWalletTile.icon} alt="" className="h-8 w-8 rounded" />
                      <span className="text-xs text-sky-100/90">
                        WalletConnect v2 (QR). Set{' '}
                        <code className="text-sky-200/90">REACT_APP_WALLETCONNECT_PROJECT_ID</code>{' '}
                        in dashboard env.
                      </span>
                      <button
                        type="button"
                        onClick={() => void runWalletConnect()}
                        disabled={wcConnecting || !walletName.trim()}
                        className="text-xs px-3 py-1.5 rounded bg-sky-600 hover:bg-sky-500 disabled:opacity-60"
                      >
                        {wcConnecting ? 'Opening…' : 'Connect with WalletConnect'}
                      </button>
                    </div>
                    <div className="mt-2 text-[11px] text-slate-300">
                      {wcSession?.providerId === activeWalletTile.provider && wcSession.address ? (
                        <span>
                          <code className="text-cyan-200/90">{wcSession.address}</code>
                          {wcSession.chainHex ? (
                            <>
                              {' '}
                              · <code className="text-cyan-200/90">{wcSession.chainHex}</code>
                            </>
                          ) : null}
                        </span>
                      ) : null}
                    </div>
                  </div>
                ) : null}
                {activeWalletTile?.panel === 'phantom' ? (
                  <div className="md:col-span-2 lg:col-span-3 rounded border border-violet-600/40 bg-violet-950/25 p-3">
                    <div className="flex flex-wrap items-center gap-3">
                      <img
                        src={`${process.env.PUBLIC_URL}/phantom-mark.svg`}
                        alt="Phantom"
                        className="h-8 w-8 rounded"
                      />
                      <span className="text-xs text-violet-100/90">
                        Phantom extension or in-app browser.
                      </span>
                      <button
                        type="button"
                        onClick={connectPhantomSolana}
                        disabled={Boolean(phantomConnecting) || !walletName.trim()}
                        className="text-xs px-3 py-1.5 rounded bg-violet-600 hover:bg-violet-500 disabled:opacity-60"
                      >
                        {phantomConnecting === 'solana' ? 'Connecting…' : 'Connect Solana'}
                      </button>
                      <button
                        type="button"
                        onClick={connectPhantomEVM}
                        disabled={Boolean(phantomConnecting) || !walletName.trim()}
                        className="text-xs px-3 py-1.5 rounded bg-violet-700 hover:bg-violet-600 disabled:opacity-60"
                      >
                        {phantomConnecting === 'evm' ? 'Connecting…' : 'Connect EVM'}
                      </button>
                      <a
                        href={phantomBrowseDeepLink() || '#'}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs px-3 py-1.5 rounded border border-violet-500 text-violet-100 hover:bg-violet-900/50"
                      >
                        Open in Phantom app
                      </a>
                    </div>
                    <div className="mt-2 text-[11px] text-slate-300 space-y-1">
                      <div>
                        {phantomSolPublicKey ? (
                          <span>
                            Solana: <code className="text-cyan-200/90">{phantomSolPublicKey}</code>
                          </span>
                        ) : (
                          <span>
                            {phantomSolInstalled
                              ? 'Solana provider ready.'
                              : 'Solana provider not detected (HTTPS or localhost required).'}
                          </span>
                        )}
                      </div>
                      <div>
                        {phantomEvmAddress ? (
                          <span>
                            EVM: <code className="text-cyan-200/90">{phantomEvmAddress}</code>
                            {phantomEvmChainHex ? (
                              <>
                                {' '}
                                · <code className="text-cyan-200/90">{phantomEvmChainHex}</code>
                              </>
                            ) : null}
                          </span>
                        ) : (
                          <span>
                            {phantomEvmInstalled
                              ? 'EVM provider ready.'
                              : 'EVM provider not detected.'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ) : null}
                <input
                  className="bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm md:col-span-2"
                  placeholder="Tags"
                  value={walletTags}
                  onChange={(e) => setWalletTags(e.target.value)}
                />
              </div>
              <button
                type="button"
                onClick={connectWallet}
                className="mt-3 bg-cyan-700 hover:bg-cyan-600 text-xs px-4 rounded py-2 font-semibold"
              >
                Connect wallet
              </button>
            </div>

            <div className="rounded-3xl border border-white/5 bg-gradient-to-br from-slate-900 via-slate-900/95 to-slate-950 p-5 shadow-xl">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <h2 className="text-base font-bold tracking-wide text-slate-100">Your wallets</h2>
                  <p className="text-xs text-slate-500 mt-1">
                    Assign each wallet to an integration or agent persona for upcoming autonomous execution routing.
                  </p>
                </div>
                <span className="text-[10px] uppercase tracking-[0.25em] font-semibold rounded-full border border-violet-400/30 bg-violet-500/10 text-violet-200 px-3 py-1">
                  {wallets.length} connected
                </span>
              </div>
              <div className="mt-4 grid grid-cols-1 xl:grid-cols-2 gap-3">
                {wallets.length === 0 ? (
                  <div className="col-span-full rounded-2xl border border-dashed border-slate-700 bg-slate-950/60 p-8 text-center">
                    <p className="text-sm text-slate-400">No wallets connected yet.</p>
                    <p className="text-xs text-slate-500 mt-1">Use the panel above to add MetaMask, Phantom, WalletConnect or any custom signer.</p>
                  </div>
                ) : null}
                {wallets.map((w) => {
                  const tile = getWalletTile(w.provider);
                  return (
                  <div
                    key={w.wallet_id}
                    className="relative rounded-2xl p-[1px] bg-gradient-to-br from-violet-500/40 via-fuchsia-500/20 to-cyan-500/40 shadow-xl"
                  >
                    <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-slate-900/95 to-slate-950 p-4 flex flex-col gap-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        {tile?.icon ? (
                          <img
                            src={tile.icon}
                            alt=""
                            className="h-10 w-10 rounded-xl object-cover border border-white/10 bg-slate-950"
                          />
                        ) : (
                          <span className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-cyan-400 flex items-center justify-center font-bold text-white">
                            {(w.display_name || w.provider || '?').slice(0, 1).toUpperCase()}
                          </span>
                        )}
                        <div className="min-w-0">
                          <div className="text-sm font-bold text-slate-100 truncate">{w.display_name}</div>
                          <div className="flex flex-wrap items-center gap-1.5 mt-1 text-[10px] uppercase tracking-wider">
                            <span className="rounded-full border border-slate-700/70 bg-slate-950/60 text-slate-300 px-2 py-0.5">
                              {w.provider}
                            </span>
                            <span className="rounded-full border border-cyan-400/30 bg-cyan-500/10 text-cyan-200 px-2 py-0.5">
                              {w.chain}
                            </span>
                            <span className={`rounded-full border px-2 py-0.5 ${
                              w.mode === 'execution_enabled'
                                ? 'border-emerald-400/40 bg-emerald-500/10 text-emerald-200'
                                : 'border-amber-400/30 bg-amber-500/10 text-amber-200'
                            }`}>
                              {w.mode === 'execution_enabled' ? 'execution' : 'watch only'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => beginEditWallet(w)}
                          className="text-[11px] px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700/60"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          disabled={removingWalletId === w.wallet_id}
                          onClick={() => removeWallet(w)}
                          className="text-[11px] px-3 py-1.5 rounded-lg bg-gradient-to-r from-rose-500 to-pink-600 hover:opacity-90 text-white shadow-md disabled:opacity-60"
                        >
                          {removingWalletId === w.wallet_id ? 'Removing…' : 'Remove'}
                        </button>
                      </div>
                    </div>
                    <div className="rounded-xl border border-white/5 bg-black/40 p-3 flex flex-wrap items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Balance</p>
                        <p className="text-lg font-bold bg-gradient-to-r from-emerald-300 via-cyan-200 to-violet-200 bg-clip-text text-transparent">
                          {walletBalances[w.wallet_id]?.text || 'Loading…'}
                        </p>
                      </div>
                      <div className="min-w-0 flex-1 text-right">
                        <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Address</p>
                        <code className="text-[11px] text-cyan-200/90 break-all">
                          {w.address}
                        </code>
                      </div>
                      <button
                        type="button"
                        onClick={() => navigator.clipboard.writeText(w.address)}
                        className="text-[10px] px-2.5 py-1 rounded-lg border border-cyan-400/30 bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/20"
                      >
                        Copy
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {(w.scopes && w.scopes.length ? w.scopes : ['wallet:read']).map((s) => (
                        <span
                          key={s}
                          className="text-[10px] rounded-full border border-slate-700/60 bg-slate-950/60 text-slate-300 px-2 py-0.5"
                        >
                          {s}
                        </span>
                      ))}
                      {(w.tags || []).map((t) => (
                        <span
                          key={t}
                          className="text-[10px] rounded-full border border-fuchsia-400/30 bg-fuchsia-500/10 text-fuchsia-200 px-2 py-0.5"
                        >
                          #{t}
                        </span>
                      ))}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-2 items-center">
                      <select
                        className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs"
                        value={selectedAppId}
                        onChange={(e) => setSelectedAppId(e.target.value)}
                      >
                        <option value="">Assign to integration (optional)</option>
                        {apps.map((app) => (
                          <option key={app.app_id} value={app.app_id}>
                            {app.name}
                          </option>
                        ))}
                      </select>
                      <input
                        className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs"
                        placeholder="Agent id (optional)"
                        value={agentId}
                        onChange={(e) => setAgentId(e.target.value)}
                      />
                      <select
                        className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs"
                        value={assignmentRole}
                        onChange={(e) => setAssignmentRole(e.target.value)}
                      >
                        <option value="execution">Execution</option>
                        <option value="treasury">Treasury</option>
                        <option value="research">Research</option>
                        <option value="risk">Risk</option>
                      </select>
                      <select
                        className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs"
                        value={assignmentScope}
                        onChange={(e) => setAssignmentScope(e.target.value)}
                      >
                        <option value="read_only">READ_ONLY</option>
                        <option value="sign_only">SIGN_ONLY</option>
                        <option value="transactional">TRANSACTIONAL</option>
                        <option value="limited_spend">LIMITED_SPEND</option>
                        <option value="protocol_only">PROTOCOL_ONLY</option>
                        <option value="token_only">TOKEN_ONLY</option>
                        <option value="time_limited">TIME_LIMITED</option>
                        <option value="chain_limited">CHAIN_LIMITED</option>
                      </select>
                      <input
                        className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs"
                        placeholder="Spend limit USD (optional)"
                        value={assignmentSpendLimit}
                        onChange={(e) => setAssignmentSpendLimit(e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => assignWallet(w.wallet_id)}
                        className="bg-emerald-700 hover:bg-emerald-600 text-xs px-3 py-1.5 rounded font-semibold"
                      >
                        Assign wallet
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      <input
                        className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs"
                        placeholder="Allowed contracts (comma-separated)"
                        value={assignmentAllowedContracts}
                        onChange={(e) => setAssignmentAllowedContracts(e.target.value)}
                      />
                      <input
                        className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs"
                        placeholder="Allowed tokens (comma-separated)"
                        value={assignmentAllowedTokens}
                        onChange={(e) => setAssignmentAllowedTokens(e.target.value)}
                      />
                      <input
                        className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs"
                        placeholder="Allowed chains (comma-separated)"
                        value={assignmentAllowedChains}
                        onChange={(e) => setAssignmentAllowedChains(e.target.value)}
                      />
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-xs">
                      <label className="inline-flex items-center gap-1 text-slate-300">
                        <input type="checkbox" checked={assignmentCanRead} onChange={(e) => setAssignmentCanRead(e.target.checked)} />
                        read
                      </label>
                      <label className="inline-flex items-center gap-1 text-slate-300">
                        <input type="checkbox" checked={assignmentCanSign} onChange={(e) => setAssignmentCanSign(e.target.checked)} />
                        sign
                      </label>
                      <label className="inline-flex items-center gap-1 text-slate-300">
                        <input
                          type="checkbox"
                          checked={assignmentCanTransact}
                          onChange={(e) => setAssignmentCanTransact(e.target.checked)}
                        />
                        transact
                      </label>
                      <label className="inline-flex items-center gap-2 text-slate-300">
                        expires
                        <input
                          type="datetime-local"
                          value={assignmentExpiresAt}
                          onChange={(e) => setAssignmentExpiresAt(e.target.value)}
                          className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs"
                        />
                      </label>
                    </div>
                    {w.mode !== 'execution_enabled' ? (
                      <p className="text-[10px] text-amber-300 rounded-lg border border-amber-400/30 bg-amber-500/10 px-2 py-1">
                        Watch-only wallet: agent can monitor but cannot execute transfers/trades until mode is execution_enabled with execution scopes.
                      </p>
                    ) : null}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
              <h2 className="text-sm font-bold uppercase text-slate-300">Edit wallet settings</h2>
              {!editingWalletId ? (
                <p className="text-xs text-slate-500 mt-2">Select a wallet and click Edit.</p>
              ) : (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  <select
                    className="bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm"
                    value={editWalletChain}
                    onChange={(e) => setEditWalletChain(e.target.value)}
                  >
                    <option value="universal">Universal</option>
                    <option value="ethereum">Ethereum</option>
                    <option value="base">Base</option>
                    <option value="arbitrum">Arbitrum</option>
                    <option value="polygon">Polygon</option>
                    <option value="bsc">BNB Chain</option>
                    <option value="solana">Solana</option>
                  </select>
                  <select
                    className="bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm"
                    value={editWalletType}
                    onChange={(e) => setEditWalletType(e.target.value)}
                  >
                    <option value="non_custodial">Non-custodial</option>
                    <option value="custodial">Custodial</option>
                  </select>
                  <select
                    className="bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm"
                    value={editWalletMode}
                    onChange={(e) => setEditWalletMode(e.target.value)}
                  >
                    <option value="watch_only">Watch-only</option>
                    <option value="execution_enabled">Execution-enabled</option>
                  </select>
                  <input
                    className="bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm"
                    placeholder="Tags"
                    value={editWalletTags}
                    onChange={(e) => setEditWalletTags(e.target.value)}
                  />
                  <input
                    className="bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm md:col-span-2"
                    placeholder="Scopes (comma-separated)"
                    value={editWalletScopes}
                    onChange={(e) => setEditWalletScopes(e.target.value)}
                  />
                  <select
                    className="bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm"
                    value={editWalletSignerType}
                    onChange={(e) => setEditWalletSignerType(e.target.value)}
                  >
                    <option value="">Signer type</option>
                    <option value="injected">Injected</option>
                    <option value="walletconnect">WalletConnect</option>
                    <option value="safe">Safe</option>
                    <option value="custody">Custody</option>
                    <option value="exchange">Exchange</option>
                  </select>
                  <input
                    className="bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm md:col-span-2"
                    placeholder="Signer reference"
                    value={editWalletSignerRef}
                    onChange={(e) => setEditWalletSignerRef(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={saveWalletSettings}
                      className="bg-cyan-700 hover:bg-cyan-600 text-xs px-4 rounded py-2 font-semibold"
                    >
                      Save settings
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingWalletId('')}
                      className="bg-slate-700 hover:bg-slate-600 text-xs px-4 rounded py-2 font-semibold"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
              <h2 className="text-sm font-bold uppercase text-slate-300">Wallet assignments</h2>
              <div className="mt-3 space-y-2">
                {walletAssignments.length === 0 ? (
                  <p className="text-xs text-slate-500">No assignments yet.</p>
                ) : null}
                {walletAssignments.map((a) => (
                  <div
                    key={a.assignment_id}
                    className="flex items-center justify-between gap-2 border border-slate-800 rounded p-2 bg-slate-950"
                  >
                    <div className="text-xs text-slate-300">
                      <span className="text-slate-500">wallet:</span> {a.wallet_id}
                      <span className="mx-2 text-slate-600">·</span>
                      <span className="text-slate-500">app:</span> {a.app_id || '—'}
                      <span className="mx-2 text-slate-600">·</span>
                      <span className="text-slate-500">agent:</span> {a.agent_id || '—'}
                      <span className="mx-2 text-slate-600">·</span>
                      <span className="text-slate-500">role:</span> {a.role}
                      <span className="mx-2 text-slate-600">·</span>
                      <span className="text-slate-500">scope:</span> {a.scope || 'read_only'}
                      {a.spend_limit_usd ? (
                        <>
                          <span className="mx-2 text-slate-600">·</span>
                          <span className="text-slate-500">limit:</span> ${a.spend_limit_usd}
                        </>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      disabled={removingAssignmentId === a.assignment_id}
                      onClick={() => removeWalletAssignment(a)}
                      className="text-xs px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 disabled:opacity-60"
                    >
                      {removingAssignmentId === a.assignment_id ? 'Removing…' : 'Remove'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {binanceOpen && (
          <Modal title="Binance — connect" onClose={closeBinanceModal}>
            <ol className="mb-4 list-decimal space-y-1 pl-4 text-xs text-slate-400">
              <li>Create API keys in Binance (read permissions for balances; enable trading only if you need execution).</li>
              <li>Paste the key and secret below. They are stored encrypted server-side.</li>
              <li>Connect stores credentials, registers the account, then verifies with a live balance fetch.</li>
            </ol>
            <div className="space-y-3 text-xs">
              <input
                className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm"
                placeholder="Display name (e.g. Binance Main)"
                value={bnDisplayName}
                onChange={(e) => setBnDisplayName(e.target.value)}
              />
              <input
                className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm"
                placeholder="Key alias (default: binance-primary)"
                value={bnKeyAlias}
                onChange={(e) => setBnKeyAlias(e.target.value)}
              />
              <input
                className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm"
                placeholder="Binance API key"
                value={bnApiKey}
                onChange={(e) => setBnApiKey(e.target.value)}
                autoComplete="off"
              />
              <input
                type="password"
                className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm"
                placeholder="Binance API secret"
                value={bnApiSecret}
                onChange={(e) => setBnApiSecret(e.target.value)}
                autoComplete="off"
              />
              {bnVerifyMsg ? (
                <p className="rounded border border-emerald-500/40 bg-emerald-950/40 px-3 py-2 text-emerald-200">{bnVerifyMsg}</p>
              ) : null}
              <button
                type="button"
                onClick={submitBinance}
                disabled={bnConnecting}
                className="w-full rounded bg-slate-100 py-2 text-sm font-semibold text-slate-900 hover:bg-white disabled:opacity-60"
              >
                {bnConnecting ? 'Connecting…' : 'Connect & verify'}
              </button>
              {bnVerifyMsg ? (
                <button
                  type="button"
                  onClick={closeBinanceModal}
                  className="w-full rounded border border-slate-600 py-2 text-sm text-slate-200 hover:bg-slate-800"
                >
                  Done
                </button>
              ) : null}
            </div>
          </Modal>
        )}

        {customOpen && (
          <Modal title="Custom connection" onClose={() => setCustomOpen(false)}>
            <p className="mb-3 text-xs text-slate-400">
              Register a placeholder account for a provider you are integrating. Use the same slug you will use in connectors
              and credentials later.
            </p>
            <div className="space-y-3 text-xs">
              <input
                className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm"
                placeholder="Display name"
                value={cuDisplayName}
                onChange={(e) => setCuDisplayName(e.target.value)}
              />
              <input
                className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm"
                placeholder="Provider id (e.g. kraken, interactive-brokers)"
                value={cuProvider}
                onChange={(e) => setCuProvider(e.target.value)}
              />
              <select
                className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm"
                value={cuAccountType}
                onChange={(e) => setCuAccountType(e.target.value)}
              >
                <option value="crypto_exchange">Crypto exchange</option>
                <option value="brokerage">Brokerage</option>
                <option value="bank">Bank</option>
                <option value="wallet">Wallet</option>
              </select>
              <input
                className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm"
                placeholder="Currency (optional — defaults by type)"
                value={cuCurrency}
                onChange={(e) => setCuCurrency(e.target.value)}
              />
              <button
                type="button"
                onClick={submitCustom}
                className="w-full rounded bg-slate-100 py-2 text-sm font-semibold text-slate-900 hover:bg-white"
              >
                Add connection
              </button>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
}

function fmtRelative(timestamp) {
  if (!timestamp) {
    return '—';
  }
  const t = new Date(timestamp);
  if (Number.isNaN(t.getTime())) {
    return '—';
  }
  const diffMs = Date.now() - t.getTime();
  if (diffMs < 0) {
    return t.toLocaleString();
  }
  const s = Math.floor(diffMs / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

/**
 * Read-only agent control plane backed by /v1/account-hub/agents.
 *
 * Each agent row is the join of: registered app (token-bearing identity) + its grant +
 * wallet assignments + recent audit. Mutations still go through the existing apps/grants/
 * wallet-assignment endpoints — this view is the operator dashboard for "who is calling MCP
 * right now and which wallets can they touch".
 */
/**
 * Unified bot timeline with filters and CSV export (Phase E).
 */
function AgentsObservabilityTimeline({ apps }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [appId, setAppId] = useState('');
  const [toolFilter, setToolFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sinceHours, setSinceHours] = useState('24');

  const loadLogs = async () => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams({ limit: '200', offset: '0' });
      if (appId) params.set('app_id', appId);
      if (toolFilter.trim()) params.set('tool', toolFilter.trim());
      if (statusFilter) params.set('status', statusFilter);
      if (sinceHours) {
        const since = new Date(Date.now() - Number(sinceHours) * 3600 * 1000).toISOString();
        params.set('since', since);
      }
      const res = await mcpGet(`/v1/account-hub/observability/logs?${params.toString()}`);
      setLogs(res.logs || []);
      setTotal(res.total || 0);
    } catch (err) {
      setError(err.message || 'Failed to load observability logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (appId) params.set('app_id', appId);
    if (toolFilter.trim()) params.set('tool', toolFilter.trim());
    if (statusFilter) params.set('status', statusFilter);
    if (sinceHours) {
      params.set('since', new Date(Date.now() - Number(sinceHours) * 3600 * 1000).toISOString());
    }
    const url = mcpApiUrl(`/v1/account-hub/observability/export.csv?${params.toString()}`);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="mt-6 rounded-2xl border border-slate-800 bg-slate-900/80 p-4 md:p-5 space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-100">Bot activity timeline</p>
          <p className="text-xs text-slate-400 mt-1 max-w-2xl">
            Standard log envelope for post-mortems: tool, broker, symbol, status, latency, and error codes — no SSH
            required.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={loadLogs}
            className="rounded-lg border border-slate-700 px-3 py-1.5 text-[11px] font-semibold text-slate-200 hover:border-cyan-400/50"
          >
            Refresh
          </button>
          <button
            type="button"
            onClick={exportCsv}
            className="rounded-lg border border-cyan-500/40 bg-cyan-500/10 px-3 py-1.5 text-[11px] font-semibold text-cyan-100 hover:bg-cyan-500/20"
          >
            Export CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        <label className="text-[10px] text-slate-500 uppercase tracking-wider">
          App
          <select
            value={appId}
            onChange={(e) => setAppId(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-800 bg-slate-950 text-xs text-slate-200 px-2 py-1.5"
          >
            <option value="">All apps</option>
            {(apps || []).map((a) => (
              <option key={a.app_id} value={a.app_id}>
                {a.name || a.app_id}
              </option>
            ))}
          </select>
        </label>
        <label className="text-[10px] text-slate-500 uppercase tracking-wider">
          Tool contains
          <input
            value={toolFilter}
            onChange={(e) => setToolFilter(e.target.value)}
            placeholder="trading.place_order"
            className="mt-1 w-full rounded-lg border border-slate-800 bg-slate-950 text-xs text-slate-200 px-2 py-1.5"
          />
        </label>
        <label className="text-[10px] text-slate-500 uppercase tracking-wider">
          Status
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-800 bg-slate-950 text-xs text-slate-200 px-2 py-1.5"
          >
            <option value="">Any</option>
            <option value="success">success</option>
            <option value="error">error</option>
          </select>
        </label>
        <label className="text-[10px] text-slate-500 uppercase tracking-wider">
          Since (hours)
          <input
            type="number"
            min="1"
            value={sinceHours}
            onChange={(e) => setSinceHours(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-800 bg-slate-950 text-xs text-slate-200 px-2 py-1.5"
          />
        </label>
      </div>

      {error ? (
        <p className="text-xs text-rose-300">{error}</p>
      ) : null}
      {loading ? (
        <p className="text-xs text-slate-500">Loading timeline…</p>
      ) : (
        <p className="text-[10px] text-slate-500">
          Showing {logs.length} of {total} events
        </p>
      )}

      <div className="overflow-x-auto rounded-xl border border-slate-800">
        <table className="min-w-full text-left text-[11px]">
          <thead className="bg-slate-950 text-slate-500 uppercase tracking-wider text-[10px]">
            <tr>
              <th className="px-3 py-2">Time</th>
              <th className="px-3 py-2">App</th>
              <th className="px-3 py-2">Tool</th>
              <th className="px-3 py-2">Broker</th>
              <th className="px-3 py-2">Symbol</th>
              <th className="px-3 py-2">Side</th>
              <th className="px-3 py-2">Qty</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">ms</th>
              <th className="px-3 py-2">Error</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/80 text-slate-300">
            {logs.map((row) => (
              <tr key={row.event_id || `${row.ts}-${row.tool}`} className="hover:bg-slate-950/50">
                <td className="px-3 py-2 whitespace-nowrap text-slate-500">
                  {row.ts ? new Date(row.ts).toLocaleString() : '—'}
                </td>
                <td className="px-3 py-2 font-mono text-[10px] text-slate-400">{row.app_id || '—'}</td>
                <td className="px-3 py-2 font-mono text-cyan-200/90">{row.tool || row.action || '—'}</td>
                <td className="px-3 py-2">{row.broker || '—'}</td>
                <td className="px-3 py-2">{row.symbol || '—'}</td>
                <td className="px-3 py-2">{row.side || '—'}</td>
                <td className="px-3 py-2">{row.qty ? row.qty : '—'}</td>
                <td className="px-3 py-2">
                  <span
                    className={
                      row.status === 'error'
                        ? 'text-rose-300 font-semibold'
                        : 'text-emerald-300'
                    }
                  >
                    {row.status || '—'}
                  </span>
                </td>
                <td className="px-3 py-2">{row.latency_ms != null ? row.latency_ms : '—'}</td>
                <td className="px-3 py-2 font-mono text-rose-300/90">{row.error_code || '—'}</td>
              </tr>
            ))}
            {!loading && logs.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-3 py-8 text-center text-slate-500">
                  No events match these filters yet. Invoke MCP tools with an app token to populate the timeline.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AgentsView({ agents, wallets, apps, loading, error }) {
  const walletById = useMemo(() => {
    const out = {};
    for (const w of wallets || []) {
      out[w.wallet_id] = w;
    }
    return out;
  }, [wallets]);

  if (loading) {
    return (
      <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-6 text-xs text-slate-400">
        Loading agents…
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-xl border border-red-900 bg-red-950/30 p-4 text-xs text-red-200">{error}</div>
    );
  }

  if (!Array.isArray(agents) || agents.length === 0) {
    return (
      <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-6 text-xs text-slate-400 space-y-2">
        <p className="text-sm font-semibold text-slate-200">No agents registered yet</p>
        <p>
          Register your first AI client (Claude, Cursor, n8n, your own bot) under{' '}
          <a href="/account-hub/apps" className="text-cyan-300 hover:underline">
            App integrations
          </a>
          . You get a unique app id and token; the same MCP URL works for every agent — identity comes from the token.
        </p>
        <p>
          Then bind a wallet to that agent under{' '}
          <a href="/account-hub/wallets" className="text-cyan-300 hover:underline">
            Wallets
          </a>{' '}
          to scope execution permissions per bot.
        </p>
        {apps?.length ? (
          <p className="text-slate-500">
            ({apps.length} app{apps.length === 1 ? '' : 's'} registered but missing from the agents feed — refresh in a few
            seconds while audit catches up.)
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {agents.map((row) => {
        const app = row.app || {};
        const grant = row.grant || null;
        const assignments = Array.isArray(row.wallet_assignments) ? row.wallet_assignments : [];
        const events = Array.isArray(row.recent_audit_events) ? row.recent_audit_events : [];
        const lastSeen = row.last_seen_at;
        const calls = row.call_count || 0;

        return (
          <div
            key={app.app_id || app.AppID}
            className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900 via-slate-900/95 to-slate-950 p-4 md:p-5 shadow-xl"
          >
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-500 via-fuchsia-500 to-cyan-400 text-white text-sm font-bold flex items-center justify-center">
                    {String(app.name || '·')[0]?.toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-100 truncate">{app.name || 'Unnamed agent'}</p>
                    <p className="text-[10px] text-slate-500 font-mono">
                      app_id <span className="text-cyan-200/80">{app.app_id || '—'}</span>
                    </p>
                  </div>
                  <span className="rounded-full border border-slate-700 bg-slate-950 px-2 py-0.5 text-[10px] uppercase tracking-wider text-slate-300">
                    {app.kind || 'agent'}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2 text-[10px] uppercase tracking-[0.18em]">
                  <span className="rounded-full border border-emerald-400/30 bg-emerald-500/10 text-emerald-200 px-2.5 py-0.5">
                    {calls} call{calls === 1 ? '' : 's'}
                  </span>
                  <span className="rounded-full border border-slate-700 bg-slate-950 text-slate-300 px-2.5 py-0.5">
                    last seen {fmtRelative(lastSeen)}
                  </span>
                  <span className="rounded-full border border-violet-400/30 bg-violet-500/10 text-violet-200 px-2.5 py-0.5">
                    {assignments.length} wallet{assignments.length === 1 ? '' : 's'}
                  </span>
                  {grant?.transactional_access ? (
                    <span className="rounded-full border border-rose-400/40 bg-rose-500/15 text-rose-200 px-2.5 py-0.5">
                      tx enabled
                    </span>
                  ) : (
                    <span className="rounded-full border border-slate-700 bg-slate-950 text-slate-400 px-2.5 py-0.5">
                      read-only
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <a
                  href="/account-hub/apps"
                  className="rounded-lg border border-slate-700 px-3 py-1.5 text-[11px] font-semibold text-slate-200 hover:border-violet-400/60 hover:text-white"
                >
                  Manage app
                </a>
                <a
                  href="/account-hub/wallets"
                  className="rounded-lg border border-slate-700 px-3 py-1.5 text-[11px] font-semibold text-slate-200 hover:border-cyan-400/60 hover:text-white"
                >
                  Wallets
                </a>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-3">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Scopes</p>
                {grant && Array.isArray(grant.scopes) && grant.scopes.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {grant.scopes.map((s) => (
                      <span
                        key={s}
                        className="rounded-md border border-slate-800 bg-slate-900 text-[10px] text-slate-200 px-2 py-0.5 font-mono"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-[11px] text-slate-500">
                    No scopes granted yet — open{' '}
                    <a href="/account-hub/apps" className="text-cyan-300 hover:underline">
                      App integrations
                    </a>{' '}
                    to grant.
                  </p>
                )}
              </div>

              <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-3">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Wallet bindings</p>
                {assignments.length === 0 ? (
                  <p className="text-[11px] text-slate-500">
                    No wallets assigned. Assign a wallet under{' '}
                    <a href="/account-hub/wallets" className="text-cyan-300 hover:underline">
                      Wallets
                    </a>{' '}
                    to give this agent execution permissions.
                  </p>
                ) : (
                  <ul className="space-y-1.5">
                    {assignments.map((a) => {
                      const w = walletById[a.wallet_id];
                      return (
                        <li
                          key={a.assignment_id}
                          className="rounded-md border border-slate-800 bg-slate-900/70 px-2.5 py-1.5 text-[11px] flex flex-wrap items-center justify-between gap-2"
                        >
                          <span className="text-slate-100 font-medium truncate">
                            {w?.display_name || a.wallet_id}
                          </span>
                          <span className="flex flex-wrap items-center gap-1.5 text-[10px] text-slate-400">
                            <span className="rounded border border-slate-800 bg-slate-950 px-1.5 py-0.5 font-mono">
                              {a.role || a.scope || 'read_only'}
                            </span>
                            {a.spend_limit_usd ? (
                              <span className="rounded border border-amber-400/30 bg-amber-500/10 text-amber-200 px-1.5 py-0.5">
                                ${Number(a.spend_limit_usd).toLocaleString()} cap
                              </span>
                            ) : null}
                            {a.expires_at ? (
                              <span className="rounded border border-slate-700 bg-slate-950 px-1.5 py-0.5">
                                until {new Date(a.expires_at).toLocaleDateString()}
                              </span>
                            ) : null}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>

            {events.length > 0 ? (
              <div className="mt-3 rounded-xl border border-slate-800 bg-slate-950/60 p-3">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Recent activity</p>
                <ul className="space-y-1 text-[11px] text-slate-300">
                  {events.map((ev) => {
                    const id = ev.event_id || ev.ts;
                    const label = ev.tool || ev.action || 'event';
                    const st = ev.status || '';
                    return (
                      <li key={id} className="flex items-center justify-between gap-3">
                        <span className="font-mono text-slate-200 truncate">
                          {label}
                          {st ? (
                            <span className={st === 'error' ? ' text-rose-300' : ' text-emerald-300'}>
                              {' '}
                              · {st}
                            </span>
                          ) : null}
                          {ev.error_code ? (
                            <span className="text-rose-400/80"> ({ev.error_code})</span>
                          ) : null}
                        </span>
                        <span className="text-slate-500 text-[10px] shrink-0">
                          {fmtRelative(ev.ts || ev.occurred_at)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
